import { PropertyType } from '@app/models/enums'
import { GeoJsonPoint } from '@app/models/GeoJsonPoint'
import { IProperty, Property } from '@app/models/Property'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { Loader } from '@googlemaps/js-api-loader'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IMapMarkerData {
  readonly name: string
  readonly gps: GeoJsonPoint
  readonly id: string
  readonly type: string
  readonly status?: string
  readonly propertyTypes?: List<PropertyType>
}
export interface IMapViewState {
  readonly markerOption: 'listing' | 'property'
  readonly listings: List<IMapMarkerData>
  readonly listingStatus: string
  readonly propertyTypes: List<PropertyType>
  readonly error?: string
  readonly isLoading: boolean
  readonly properties: List<IMapMarkerData>
  readonly totalItems: number
}

const MapViewStateRecord = Record({
  propertyTypes: List<PropertyType>(),
  error: undefined,
  isLoading: false,
  properties: List<Property>(),
  listings: List<IMapMarkerData>(),
  totalItems: 0,
  markerOption: 'property',
  listingStatus: 'Active',
})

export class MapViewState extends MapViewStateRecord implements IMapViewState {
  public static fromJS(initialState?: Partial<IMapViewState>) {
    if (!initialState) {
      return new MapViewState()
    }

    const { properties, listings, ...rest } = initialState
    return new MapViewState({
      properties: properties
        ? List(properties.map((i) => Property.fromJS(i)))
        : undefined,
      listings: listings ? List(listings) : undefined,
      ...rest,
    })
  }

  public readonly markerOption: 'listing' | 'property'
  public readonly error?: string
  public readonly isLoading: boolean
  public readonly properties: List<IMapMarkerData>
  public readonly propertyTypes: List<PropertyType>
  public readonly totalItems: number
  public readonly displayOption: string
  public readonly listings: List<IMapMarkerData>
  public readonly listingStatus: string

  constructor(props?: Partial<MapViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum MapViewActionTypes {
  LoadProperties = 'MAP_VIEW::LOAD_PROPERTIES',
  LoadPropertiesFailure = 'MAP_VIEW::LOAD_PROPERTIES_FAILURE',
  LoadPropertiesRequest = 'MAP_VIEW::LOAD_PROPERTIES_REQUEST',
  LoadPropertiesSuccess = 'MAP_VIEW::LOAD_PROPERTIES_SUCCESS',
  LoadListings = 'MAP_VIEW::LOAD_LISTINGS',
  LoadListingsFailure = 'MAP_VIEW::LOAD_LISTINGS_FAILURE',
  LoadListingsRequest = 'MAP_VIEW::LOAD_LISTINGS_REQUEST',
  LoadListingsSuccess = 'MAP_VIEW::LOAD_LISTINGS_SUCCESS',
  SetPropertyTypes = 'MAP_VIEW::SET_PROPERTY_TYPES',
  SetMarkerOption = 'MAP_VIEW::SET_MARKER_OPTION',
  SetListingStatus = 'MAP_VIEW::SET_LISTING_STATUS',
}

interface ILoadListingsAction {
  loadComplete?: () => void
  type: MapViewActionTypes.LoadListings
}

interface ILoadListingsFailureAction {
  error: string
  type: MapViewActionTypes.LoadListingsFailure
}

interface ILoadListingsRequestAction {
  type: MapViewActionTypes.LoadListingsRequest
}

interface ILoadListingsSuccessAction {
  listings: IMapMarkerData[]
  totalItems: number
  type: MapViewActionTypes.LoadListingsSuccess
}

interface ILoadPropertiesAction {
  loadComplete?: () => void
  type: MapViewActionTypes.LoadProperties
}

interface ILoadPropertiesFailureAction {
  error: string
  type: MapViewActionTypes.LoadPropertiesFailure
}

interface ILoadPropertiesRequestAction {
  type: MapViewActionTypes.LoadPropertiesRequest
}

interface ILoadPropertiesSuccessAction {
  properties: Property[]
  totalItems: number
  type: MapViewActionTypes.LoadPropertiesSuccess
}

interface ISetPropertyTypesAction {
  propertyTypes: List<PropertyType>
  type: MapViewActionTypes.SetPropertyTypes
}

interface ISetMarkerOptionAction {
  markerOption: string
  type: MapViewActionTypes.SetMarkerOption
}

interface ISetListingStatusAction {
  listingStatus: string
  type: MapViewActionTypes.SetListingStatus
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadListingsFailureAction
  | ILoadListingsSuccessAction
  | ILoadListingsRequestAction
  | ILoadPropertiesRequestAction
  | ILoadPropertiesFailureAction
  | ILoadPropertiesRequestAction
  | ILoadPropertiesSuccessAction
  | ISetMarkerOptionAction
  | ISetPropertyTypesAction
  | ISetListingStatusAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadListings(loadComplete?: () => void): ILoadListingsAction {
  return {
    loadComplete,
    type: MapViewActionTypes.LoadListings,
  }
}

function loadListingsFailure(error: string): ILoadListingsFailureAction {
  return {
    error,
    type: MapViewActionTypes.LoadListingsFailure,
  }
}

function loadListingsRequest(): ILoadListingsRequestAction {
  return {
    type: MapViewActionTypes.LoadListingsRequest,
  }
}

function loadListingsSuccess(
  listings: IMapMarkerData[],
  totalItems: number,
): ILoadListingsSuccessAction {
  return {
    listings,
    totalItems,
    type: MapViewActionTypes.LoadListingsSuccess,
  }
}

function loadProperties(loadComplete?: () => void): ILoadPropertiesAction {
  return {
    loadComplete,
    type: MapViewActionTypes.LoadProperties,
  }
}

function loadPropertiesFailure(error: string): ILoadPropertiesFailureAction {
  return {
    error,
    type: MapViewActionTypes.LoadPropertiesFailure,
  }
}

function loadPropertiesRequest(): ILoadPropertiesRequestAction {
  return {
    type: MapViewActionTypes.LoadPropertiesRequest,
  }
}

function loadPropertiesSuccess(
  properties: Property[],
  totalItems: number,
): ILoadPropertiesSuccessAction {
  return {
    properties,
    totalItems,
    type: MapViewActionTypes.LoadPropertiesSuccess,
  }
}

function setPropertyTypes(
  propertyTypes: List<PropertyType>,
): ISetPropertyTypesAction {
  return {
    propertyTypes,
    type: MapViewActionTypes.SetPropertyTypes,
  }
}

function setMarkerOption(markerOption: string): ISetMarkerOptionAction {
  return {
    markerOption,
    type: MapViewActionTypes.SetMarkerOption,
  }
}

function SetListingStatus(listingStatus: string): ISetListingStatusAction {
  return {
    listingStatus,
    type: MapViewActionTypes.SetListingStatus,
  }
}

export const actionCreators = {
  loadListings,
  loadProperties,
  setMarkerOption,
  setPropertyTypes,
  SetListingStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<MapViewState> = (
  state: MapViewState = new MapViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case MapViewActionTypes.LoadListingsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as MapViewState

    case MapViewActionTypes.LoadListingsRequest:
      return state.set('isLoading', true) as MapViewState

    case MapViewActionTypes.LoadListingsSuccess:
      const listings = List(action.listings)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('listings', listings)
        .set('totalItems', action.totalItems) as MapViewState

    case MapViewActionTypes.LoadPropertiesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as MapViewState

    case MapViewActionTypes.LoadPropertiesRequest:
      return state.set('isLoading', true) as MapViewState

    case MapViewActionTypes.LoadPropertiesSuccess:
      const properties = List(action.properties)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('properties', properties)
        .set('totalItems', action.totalItems) as MapViewState

    case MapViewActionTypes.SetPropertyTypes:
      return state.set('propertyTypes', action.propertyTypes) as MapViewState

    case MapViewActionTypes.SetMarkerOption:
      return state.set('markerOption', action.markerOption) as MapViewState

    case MapViewActionTypes.SetListingStatus:
      return state.set('listingStatus', action.listingStatus) as MapViewState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadPropertiesSaga(action: ILoadPropertiesAction) {
  try {
    yield put(loadPropertiesRequest())

    const currentState: MapViewState = yield select(
      (state: ApplicationState) => state.mapView,
    )

    const url = `/api/Properties/All`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const properties = result.results.map((c: any) => {
        const property = Property.fromJS(c)
        return {
          name: property.name,
          gps: property.gps,
          id: property.id,
          propertyTypes: property.propertyTypes,
          type: 'property',
        }
      })
      yield put(loadPropertiesSuccess(properties, result.count))
    } else {
      yield put(loadPropertiesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPropertiesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadListingsSaga(action: ILoadListingsAction) {
  try {
    yield put(loadListingsRequest())

    const currentState: MapViewState = yield select(
      (state: ApplicationState) => state.mapView,
    )

    const url = `/api/Listings/All`
    const result = yield makeAuthenticatedRequest(url)
    if (result.success === true) {
      const listings = result.results.map((listing: any) => {
        return {
          name: listing.name,
          gps: listing.gps,
          id: listing.id,
          status: listing.status,
          type: 'listing',
        }
      })
      yield put(loadListingsSuccess(listings, result.count))
    } else {
      yield put(loadListingsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadListingsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadListings() {
  yield takeLatest(MapViewActionTypes.LoadListings, loadListingsSaga)
}

function* watchLoadProperties() {
  yield takeLatest(MapViewActionTypes.LoadProperties, loadPropertiesSaga)
}

export function* mapViewRootSaga() {
  yield fork(watchLoadListings)
  yield fork(watchLoadProperties)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): MapViewState =>
    state.mapView

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectListings = () =>
  createSelector(selectModuleState(), (mState) => mState.listings)

export const selectProperties = () =>
  createSelector(selectModuleState(), (mState) => mState.properties)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)

export const selectPropertyTypes = () =>
  createSelector(selectModuleState(), (mState) => mState.propertyTypes)

export const selectMarkerOption = () =>
  createSelector(selectModuleState(), (mState) => mState.markerOption)

export const selectListingStatus = () =>
  createSelector(selectModuleState(), (mState) => mState.listingStatus)
